import { usePagination } from "@ajna/pagination"
import { SearchIcon } from "@chakra-ui/icons"
import {
	Box,
	Button,
	Flex,
	Input,
	InputGroup,
	InputRightAddon,
	Tag,
	Text,
	useDisclosure,
} from "@chakra-ui/react"
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import ReactSelect from "react-select"
import { CustomPagination } from "src/components/shared/CustomPagination"
import { SelectOption } from "src/components/shared/ReactSelect"
import { InputLabel } from "src/components/ui/InputLabel"
import { useContactCategoryListService, useContactListService } from "src/hooks"
import { fetchGujaratiSuggestions } from "src/utils/helpers"

import { IContact } from "../../../domain/entities"
import { DashboardWrapper } from "../../wrappers/DashboardWrapper"
import { ContactAddDrawerFormController } from "./ContactAddDrawerForm"
import { ContactDeleteDialogController } from "./ContactDeleteDialogController"
import { ContactListController } from "./ContactList"
import { ContactUpdateDrawerFormController } from "./ContactUpdateDrawerForm"

export const ContactsPage: FC = () => {
	const { contactList, isLoading, fetchContactList } = useContactListService()
	const { contactCategoryList, fetchContactCategoryList } =
		useContactCategoryListService()
	const [isContactDeleteDialogOpen, setIsContactDeleteDialogOpen] = useState(false)
	const contactAddDrawerDisclosure = useDisclosure()
	const contactUpdateDrawerDisclosure = useDisclosure()

	const pagination = usePagination({
		initialState: { currentPage: 1, pageSize: 15 },
	})

	const [searchText, setSearchText] = useState("")
	const [selectedContactCategory, setSelectedContactCategory] =
		useState<SelectOption | null>({
			label: "All",
			value: null,
		})
	const contactCategoryOptions = useMemo(() => {
		return [
			{
				label: "All",
				value: null,
			},
			...contactCategoryList.map((contactCategory) => ({
				label: contactCategory.name,
				value: contactCategory.id,
			})),
		]
	}, [contactCategoryList])

	const fetchContacts = useCallback(() => {
		fetchContactList({
			fetch: { category: true },
			pagination: {
				page: pagination.currentPage,
				limit: 15,
			},
			search: searchText,
			categoryId: selectedContactCategory?.value ?? undefined,
		})
	}, [fetchContactList, pagination.currentPage, searchText, selectedContactCategory])

	const [gujaratiSuggestions, setGujaratiSuggestions] = useState<string[]>([])

	const getGujaratiSuggestions = useCallback(async (searchText) => {
		const suggestions = await fetchGujaratiSuggestions(searchText)
		setGujaratiSuggestions(suggestions)
	}, [])

	useEffect(() => {
		fetchContacts()
		fetchContactCategoryList()
	}, [fetchContacts, fetchContactCategoryList])

	const updateContactRef = useRef<IContact>()
	const deleteContactRef = useRef<IContact>()

	return (
		<DashboardWrapper>
			<Box padding={2} pb={0}>
				<Flex justifyContent="space-between" alignItems="center">
					<Text fontSize="2xl" fontWeight="bold">
						Contacts
					</Text>
					<Button
						size="sm"
						onClick={contactAddDrawerDisclosure.onOpen}
						colorScheme="blue"
					>
						Add Contact
					</Button>
				</Flex>
			</Box>
			<Box px={2} mb={4}>
				<Flex gridColumnGap={2} align="flex-end">
					<Box>
						<InputGroup width={"fit-content"}>
							<Input
								value={searchText}
								onChange={(e) => {
									setSearchText(e.target.value)
									getGujaratiSuggestions(e.target.value)
								}}
								type="text"
								placeholder="Search"
							/>
							<InputRightAddon>
								<SearchIcon />
							</InputRightAddon>
						</InputGroup>
					</Box>
					<Box minW={"230px"}>
						<InputLabel label="ContactCategory" />
						<ReactSelect
							onChange={(newValue) => {
								setSelectedContactCategory(
									contactCategoryOptions.find(
										(el) => el.value === newValue?.value,
									) ?? contactCategoryOptions[0],
								)
							}}
							value={selectedContactCategory}
							options={contactCategoryOptions}
							isSearchable={false}
						/>
					</Box>
				</Flex>
				{gujaratiSuggestions.length ? (
					<Box mt={1}>
						{gujaratiSuggestions.map((el) => (
							<Tag
								colorScheme={"green"}
								backgroundColor={"green.50"}
								variant="outline"
								_hover={{
									backgroundColor: "green.100",
								}}
								cursor="pointer"
								margin={0.5}
								onClick={() => {
									pagination.setCurrentPage(1)
									setSearchText(el)
									setGujaratiSuggestions([])
								}}
							>
								{el}
							</Tag>
						))}
					</Box>
				) : null}
			</Box>
			<ContactListController
				list={contactList}
				isLoading={isLoading}
				onUpdate={(contact: IContact) => {
					updateContactRef.current = contact
					contactUpdateDrawerDisclosure.onOpen()
				}}
				onDelete={(contact: IContact) => {
					deleteContactRef.current = contact
					setIsContactDeleteDialogOpen(true)
				}}
				onAddClick={contactAddDrawerDisclosure.onOpen}
			/>
			<CustomPagination
				pagination={pagination}
				isNextDisabled={contactList.length === 0}
			/>
			{contactAddDrawerDisclosure.isOpen ? (
				<ContactAddDrawerFormController
					{...contactAddDrawerDisclosure}
					onSuccess={() => fetchContacts()}
				/>
			) : null}
			{updateContactRef.current && contactUpdateDrawerDisclosure.isOpen && (
				<ContactUpdateDrawerFormController
					{...contactUpdateDrawerDisclosure}
					contact={updateContactRef.current}
					onSuccess={() => fetchContacts()}
				/>
			)}
			{deleteContactRef.current && (
				<ContactDeleteDialogController
					isOpen={isContactDeleteDialogOpen}
					setIsOpen={setIsContactDeleteDialogOpen}
					contact={deleteContactRef.current}
					onSuccess={() => fetchContacts()}
				/>
			)}
		</DashboardWrapper>
	)
}
